// pages/ContributorRecordPage.jsx — Anamoria SPA
// v1.11 — NEW-163 Increment B (2026-10-30): a member can respond to the leader's note.
//   - Reads `?note=<group_notes id>` from location.search (it is in the URL, so it survives refresh and the
//     voice/video round trip) and quotes the note at the top of the capture + review screens
//     (RespondingToNote; copy v1.0 B-8). The note is resolved from space.groupNotes already loaded here.
//   - handleSave: the create payload carries `groupNoteId` only when the id resolves against
//     space.groupNotes (unknown → untagged; the server validates regardless, 400 INVALID_GROUP_NOTE).
//   - Share to the group with a loved-one name from ContributorSaveActions v1.4 (opts.lovedOneName):
//     PATCH {prefix}/{spaceId} { lovedOneName } (the live body-action) BEFORE the create so the
//     attribution join finds it on first render. If that PATCH fails nothing is created (the page's
//     standard error; the member can retry). Non-group / flag-off: payload byte-identical to v1.10.
//   SPA build spec v0.2 §6 (C-e confirmed at build: single create body, space GET on mount).
// E3b (Consent PR E, Oct 4, 2026): edit mode passes space/api/keepCopyExcluded to ContributorEditSharing;
//   handleKeepCopy PATCHes keepCopyExcluded (+ keepCopyShownHash on re-include); share state carries keepCopyExcluded.
// E3a (Consent PR E, Oct 4, 2026): handleSave(visibilityPref, keepCopyOpts) forwards keepCopy + keepCopyShownHash
//   from ContributorSaveActions on a first share; NOTICE_CHANGED/NOTICE_UNAVAILABLE -> plain error. Dark while the flag is off.
// v1.10 — PR-7a.2 polish: Re-record sits just BELOW the recording card (right-aligned, tight),
//   grouped with the card so the reviewBody 28px gap doesn't push it away. (2026-09-05)
// v1.8 — PR-7a.2 polish: audio edit — Re-record is a plain link (tight), and the title/label sits
//   above the player (edit mode only; create layout unchanged). (2026-09-05)
// v1.7 — PR-7a.2: edit mode moves Re-record up under the player and uses the shared compact
//   Cancel/Save changes footer (create flow unchanged). (2026-09-05)
// v1.6 — PR-7a.1: sharing UX clarity. Sharing saves immediately; unsaved content edits keep the user here
//   (nudged), otherwise a toast confirms and drops back to the feed. Edit-mode button reads 'Save changes'. (2026-09-05)
// v1.5 — PR-7a / NS-6 + NEW-74: edit mode now shows sharing controls (ContributorEditSharing):
//   change tier / share / make-private on the contributor's own voice memory, via a visibility-only
//   PATCH (independent of the content save). Group-gated; pull-back silent (PR-4b). ALSO NEW-74: in
//   edit mode the Review header back now returns to the feed instead of re-recording. (2026-09-05)
// v1.4 — PR-6 / NEW-64 private-by-default: "Save just for me" now saves a
//   PRIVATE (contributor_only) voice memory in THIS space for EVERY contributor
//   via the contributor create pipeline (handleSave('contributor_only')) — no
//   own-space copy. Removed the own-space owner pipeline (handleSaveJustForMe,
//   ownerApi/createApiClient) and the ownSpaceId gating on the button.
//   ContributorSaveActions now takes onSavePrivate. (2026-09-04)
// v1.3 — B1b: group-spaces two-button save (dark). When space.groupSpacesEnabled,
//   the review screen shows "Save just for me" (private draft → own space via the
//   owner create pipeline, linked only) + "Share this memory" (scope picker →
//   visibilityPref owner_contributor_only|group on the contributor create). Flag
//   OFF or edit mode → unchanged single "Add to {space}'s space" button
//   (byte-identical). New shared <ContributorSaveActions>. Copy = Contributor
//   Sharing Options v0.7. (July 26, 2026)
// v1.2 — Account-link access mode (May 31, 2026)
//   - Added `mode` prop ('session' default → no regression). Data layer from
//     getContributorAccess(mode, getAccessTokenSilently); contribute paths use
//     the bound prefix; navigation + BottomNav are prefix/mode-aware; expiry
//     copy is mode-aware; linked mode uses no sessionStorage token. The
//     /media/playback call stays unprefixed (shared JWT-or-session route).
//
// v1.1 — Edit mode for voice memories (May 25, 2026)
//
// Changes from v1.0:
//   - Reads location.state.editMemory on mount. If present, enters review
//     mode with existing audio (fetched via /media/playback/:key), pre-populates
//     label, changes subtitle to "Edit voice note", saves via PATCH.
//   - "Re-record" works as before (new recording replaces old).
//   - Only sends s3Key/duration/mimeType in PATCH when audio was re-recorded.
//   - No post-save gating — straight back to feed.
//
// v1.0 — Session 3 (April 19, 2026)
//
// Contributor voice capture page. Parallel to owner RecordPage.jsx v5.4 but
// adapted for contributor context.
//
// Route: /contribute/:spaceId/record
//
// CSS: Reuses RecordPage.module.css per Session 3 Plan v1.1 decision B2
// (new component, shared CSS module). Zero CSS duplication.
//
// Differences from owner RecordPage.jsx v5.4:
//   - Auth: createContributorApiClient (session token) instead of Auth0 JWT
//   - Space fetch: GET /contribute/:spaceId
//   - Upload URL: POST /contribute/:spaceId/upload-url — spaceId in path, body is
//     {mimeType, mediaType} (differs from owner POST /media/upload-url which
//     takes spaceId in body)
//   - Memory create: POST /contribute/:spaceId/memories with {type:'voice',
//     s3Key, mimeType, duration, title} — no isPrivate, no promptId
//   - No edit mode — edit flow deferred to Session 3.5 (decision E2).
//     All edit-related state, effects, and render branches removed.
//   - No privacy toggle — contributor memories always shared.
//   - No prompt skip — hardcoded generic prompt per decision P2.
//   - No SuccessScreen — saved pill + navigate to feed (S2).
//   - No post-save gating (reminder/feedback) — not applicable to contributors.
//   - LovedOneBar reused unchanged (pure props, no auth coupling).
//   - BottomNav → ContributorBottomNav (3-button, no Invite).
//
// Structure preserved from owner:
//   - useRecorder hook for MediaRecorder API
//   - Circle tap: idle → recording → paused → recording
//   - Stop button to enter review
//   - Review screen: playback + label input + save/re-record
//   - Waveform bars (decorative, 18-bar pattern matches owner)
//   - Blob preview via URL.createObjectURL with revoke cleanup
//
// MIME handling matches owner: strip codec suffix via split(';')[0]; Lambda
// validates against ALLOWED_MIME_TYPES.voice: ['audio/webm', 'audio/mp4',
// 'audio/ogg']. If Lambda allowlist changes, update both RecordPage files.

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useRecorder } from '../hooks/useRecorder';
import { getContributorAccess } from '../api/contributorAccess';
import LovedOneBar from '../components/LovedOneBar';
import ContributorBottomNav from '../components/ContributorBottomNav';
import ContributorSaveActions from '../components/ContributorSaveActions';
import RespondingToNote from '../components/RespondingToNote';   // v1.11 (NEW-163)
import { readNoteParam, findGroupNote } from '../utils/contributorCapture';   // v1.11 (NEW-163)
import ContributorEditSharing from '../components/ContributorEditSharing';
import ContributorEditActions from '../components/ContributorEditActions';
import styles from './RecordPage.module.css';

const TOAST_DURATION_MS = 2500;

/* ═══════════════════════════════════════
   CONTRIBUTOR RECORD PAGE
   ═══════════════════════════════════════ */

export default function ContributorRecordPage({ mode = 'linked' }) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // v1.11 (NEW-163): the group note being responded to, from ?note= (null when absent).
  const groupNoteId = readNoteParam(new URLSearchParams(location.search || ''));
  const { getAccessTokenSilently } = useAuth0();

  const navBase = '/contributing';
  const expiredMsg = 'Your session has expired. Please sign in again.';
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );

  // v1.1: Edit mode — pre-populate from existing memory
  const editMemory = location.state?.editMemory || null;
  const isEditMode = !!editMemory;

  const {
    recordingState,
    audioBlob,
    mimeType,
    duration,
    durationFormatted,
    error: recorderError,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reRecord,
  } = useRecorder();

  /* ─── Space data ─── */
  const [space, setSpace] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    // Track B (B-2): linked-only; auth via Auth0 JWT behind ProtectedRoute.
    let cancelled = false;
    async function load() {
      setLoadingData(true);
      try {
        const data = await access.api.get(`${access.prefix}/${spaceId}`);
        if (!cancelled) setSpace(data);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setLoadError(expiredMsg);
        } else {
          setLoadError('Could not load this space.');
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, access, expiredMsg]);

  /* ─── Review + save state ─── */
  const [memoryLabel, setMemoryLabel] = useState(editMemory?.title || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [s3KeySaved, setS3KeySaved] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [showSavedPill, setShowSavedPill] = useState(false);
  const [audioReRecorded, setAudioReRecorded] = useState(false);
  const [wantsReRecord, setWantsReRecord] = useState(false);

  const previewAudioRef = useRef(null);
  const toastTimerRef = useRef(null);

  /* ─── Derive blob preview URL ─── */
  useEffect(() => {
    if (audioBlob && recordingState === 'stopped') {
      const url = URL.createObjectURL(audioBlob);
      setPreviewUrl(url);
      setAudioReRecorded(true);
      setWantsReRecord(false);
      return () => URL.revokeObjectURL(url);
    }
  }, [audioBlob, recordingState]);

  /* ─── v1.1: Fetch existing audio URL in edit mode ─── */
  useEffect(() => {
    if (!isEditMode || !editMemory?.voiceNote?.s3Key) return;
    let cancelled = false;
    async function loadExistingAudio() {
      try {
        const api = access.api;
        const data = await api.get(`/media/playback/${encodeURIComponent(editMemory.voiceNote.s3Key)}`);
        if (!cancelled) setPreviewUrl(data.playbackUrl);
      } catch (err) {
        console.error('Failed to load existing audio:', err);
      }
    }
    loadExistingAudio();
    return () => { cancelled = true; };
  }, [isEditMode, editMemory]);

  /* ─── Playback control ─── */
  const togglePlayback = useCallback(() => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); } else { audio.play(); }
    setPlaying(!playing);
  }, [playing]);

  useEffect(() => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    function handleEnded() { setPlaying(false); }
    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  });

  /* ─── Cleanup toast timer on unmount ─── */
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ─── Save handler ─── */
  // E3a: keepCopyOpts = { keepCopy: true, keepCopyShownHash } from the share picker (first share), else undefined.
  const handleSave = useCallback(async (visibilityPref, keepCopyOpts) => {
    // In edit mode without re-record, save label-only changes
    if (isEditMode && !audioReRecorded) {
      setUploadError('');
      setUploading(true);
      try {
        const api = access.api;
        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, {
          title: memoryLabel.trim() || null,
        });
        setUploading(false);
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      } catch (err) {
        console.error('Save error:', err);
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setUploadError(expiredMsg);
        } else {
          setUploadError("Something didn't save. Please try again.");
        }
        setUploading(false);
      }
      return;
    }

    // Create or re-record: need audioBlob
    if (!audioBlob) return;
    setUploadError('');
    setUploading(true);

    const baseMimeType = (mimeType || 'audio/webm').split(';')[0];

    try {
      // v1.11 (NEW-163): resolve the note being responded to against the space's notes; unknown → untagged.
      const respondNoteId = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true && findGroupNote(space.groupNotes, groupNoteId)) ? groupNoteId : null;
      // v1.11 (NEW-163): loved-one name entered on the share step → save it BEFORE the create (body-action
      // PATCH on the contributor space route), so "remembering {name}" is there on first render.
      if (!isEditMode && visibilityPref === 'group' && typeof keepCopyOpts?.lovedOneName === 'string') {
        await access.api.patch(`${access.prefix}/${spaceId}`, { lovedOneName: keepCopyOpts.lovedOneName });
      }
      const api = access.api;

      // 1. Upload new audio
      const uploadData = await api.post(`${access.prefix}/${spaceId}/upload-url`, {
        mimeType: baseMimeType,
        mediaType: 'voice',
      });

      await api.putS3(uploadData.uploadUrl, audioBlob, baseMimeType);
      setS3KeySaved(uploadData.s3Key);

      if (isEditMode) {
        // v1.1: PATCH existing memory with new audio
        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, {
          s3Key: uploadData.s3Key,
          mimeType: baseMimeType,
          duration: Math.round(duration),
          title: memoryLabel.trim() || null,
        });
      } else {
        // Create new voice memory
        await api.post(`${access.prefix}/${spaceId}/memories`, {
          type: 'voice',
          s3Key: uploadData.s3Key,
          mimeType: baseMimeType,
          duration: Math.round(duration),
          ...(memoryLabel.trim() ? { title: memoryLabel.trim() } : {}),
          // B1b (dark): send the chosen share tier only when group spaces is ON;
          // omitted entirely otherwise → payload byte-identical to today.
          ...(space?.groupSpacesEnabled && typeof visibilityPref === 'string' ? { visibilityPref } : {}),
          ...(keepCopyOpts?.keepCopy === true && keepCopyOpts.keepCopyShownHash ? { keepCopy: true, keepCopyShownHash: keepCopyOpts.keepCopyShownHash } : {}),   // E3a
          ...(respondNoteId ? { groupNoteId: respondNoteId } : {}),   // v1.11 (NEW-163): only when the id resolves in space.groupNotes
        });
      }

      // Show saved pill, then navigate to feed.
      setUploading(false);
      setShowSavedPill(true);
      toastTimerRef.current = setTimeout(() => {
        navigate(`${navBase}/${spaceId}/memories`, { replace: true });
      }, TOAST_DURATION_MS);
    } catch (err) {
      console.error('Save error:', err);
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
        setUploadError(expiredMsg);
      } else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') {
        // E3a: the keep-a-copy wording changed while they were reading (server rolled the save back).
        setUploadError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");
      } else if (err.error === 'S3_UPLOAD_FAILED') {
        setUploadError('Upload failed. Please check your connection and try again.');
      } else if (s3KeySaved) {
        setUploadError('Recording uploaded but save failed. Tap "Save" to retry.');
      } else {
        setUploadError('Upload failed. Please try again.');
      }
      setUploading(false);
    }
  }, [audioBlob, mimeType, duration, memoryLabel, spaceId, s3KeySaved, navigate, navBase, isEditMode, editMemory, audioReRecorded, access, expiredMsg, space, groupNoteId]);

  // PR-7a: edit-mode sharing state (seeded from the memory being edited; updated from the PATCH response).
  const [share, setShare] = useState({ visibilityPref: editMemory?.visibilityPref, reviewStatus: editMemory?.reviewStatus, keepCopyExcluded: editMemory?.keepCopyExcluded });   // E3b
  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingNotice, setSharingNotice] = useState(false);
  const contentDirty = audioReRecorded || (memoryLabel !== (editMemory?.title || ''));
  const applySharing = async (body, failMsg) => {
    if (!editMemory) return;
    setSharingBusy(true); setUploadError(''); setSharingNotice(false);
    try {
      const res = await access.api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, body);
      setShare((prev) => ({ visibilityPref: res.visibilityPref, reviewStatus: res.reviewStatus, keepCopyExcluded: res.keepCopyExcluded ?? prev.keepCopyExcluded }));   // E3b: response carries keepCopyExcluded only after a change
      if (contentDirty) {
        setSharingNotice(true);
      } else {
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      }
    } catch (err) {
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) setUploadError(expiredMsg);
      else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') setUploadError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");   // E3b
      else setUploadError(failMsg);
    } finally { setSharingBusy(false); }
  };
  const handleShareTier = (vp) => applySharing({ visibilityPref: vp }, "We weren't able to update who can see this. When you're ready, please try again.");
  const handleMakePrivate = () => applySharing({ makePrivate: true }, "We weren't able to stop sharing that memory. When you're ready, please try again.");
  const handleKeepCopy = (excluded, shownHash) => applySharing(excluded ? { keepCopyExcluded: true } : { keepCopyExcluded: false, keepCopyShownHash: shownHash }, "We weren't able to change that. When you're ready, please try again.");   // E3b

  /* ─── Circle tap handler ─── */
  const handleCircleTap = useCallback(() => {
    if (recordingState === 'idle') startRecording();
    else if (recordingState === 'recording') pauseRecording();
    else if (recordingState === 'paused') resumeRecording();
  }, [recordingState, startRecording, pauseRecording, resumeRecording]);

  /* ─── Class composition (same as owner) ─── */
  const circleClass = [
    styles.circle,
    recordingState === 'recording' ? styles.circleRecording : '',
    recordingState === 'paused' ? styles.circlePaused : '',
  ].filter(Boolean).join(' ');

  const ringClass = [
    styles.ring,
    recordingState === 'recording' ? styles.ringBreathing : '',
    recordingState === 'paused' ? styles.ringDimmed : '',
  ].filter(Boolean).join(' ');

  const timerClass = [
    styles.timer,
    recordingState === 'recording' ? styles.timerActive : '',
  ].filter(Boolean).join(' ');

  const hintText =
    recordingState === 'recording' ? 'Tap to pause · 5:00 max' :
    recordingState === 'paused'    ? 'Tap to resume · 5:00 max' :
                                     'Tap the circle to start · 5:00 max';

  const stateLabel =
    recordingState === 'recording' ? 'Recording...' :
    recordingState === 'paused'    ? 'Paused' :
                                     'Ready';

  const barSubtitle =
    isEditMode && recordingState !== 'stopped' && !audioReRecorded ? 'Edit voice note' :
    recordingState === 'stopped'   ? 'Review before saving' :
    recordingState === 'recording' ? 'Recording...' :
    recordingState === 'paused'    ? 'Paused' :
                                     'Ready to record';

  const spaceName = space?.spaceName || 'this space';
  // v1.11 (NEW-163): the quoted leader note (B-8) when responding; null otherwise / in edit mode.
  const respondQuote = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true) ? <RespondingToNote groupNotes={space?.groupNotes} noteId={groupNoteId} leaderName={space?.ownerName} /> : null;
  const spacePhotoUrl = space?.photoUrl || null;

  /* ═══════════════════════════════════════════════════════════
     RENDER ORDER (mirrors owner: loading → uploading → saved
     → review → record). No edit branches.
     ═══════════════════════════════════════════════════════════ */

  // ─── 1. Loading ───
  if (loadingData) {
    return (
      <div className={styles.loadingPage}>
        <div className="app-loading-spinner" />
      </div>
    );
  }

  // ─── 2. Load error (pre-space) ───
  if (loadError && !space) {
    return (
      <div className={styles.loadingPage}>
        <p style={{ padding: '24px', textAlign: 'center' }}>{loadError}</p>
      </div>
    );
  }

  // ─── 3. Uploading ───
  if (uploading) {
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle="Saving..."
          onBack={() => {}}
          backLabel="Saving in progress"
        />
        <div className={styles.uploadingContent}>
          <div className="app-loading-spinner" />
          <p className={styles.uploadingText}>Saving your recording...</p>
        </div>
        <ContributorBottomNav spaceId={spaceId} activeTab="record" mode={mode} videoEnabled={space?.contributorVideoEnabled} />
      </div>
    );
  }

  // ─── 4. Review (after recording stops OR in edit mode with existing audio) ───
  if ((recordingState === 'stopped' && audioBlob) || (isEditMode && previewUrl && recordingState === 'idle' && !wantsReRecord)) {
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle="Review before saving"
          onBack={isEditMode ? () => navigate(`${navBase}/${spaceId}/memories`, { replace: true }) : reRecord}
          backLabel={isEditMode ? 'Back to memories' : 'Back to recording'}
        />
        {respondQuote}

        {/* Hardcoded generic prompt banner (no skip — P2) */}
        <div className={styles.promptWrap}>
          <HardcodedPromptBanner spaceName={spaceName} />
        </div>

        <div className={styles.reviewBody}>
          {/* Edit: title/label above the recording */}
          {isEditMode && (
            <div className={styles.settingsCard}>
              <div className={styles.settingsInputRow}>
                <input
                  type="text"
                  className={styles.labelInput}
                  placeholder="Label (optional) — e.g., Advice, Funny moments..."
                  value={memoryLabel}
                  onChange={(e) => setMemoryLabel(e.target.value)}
                />
              </div>
            </div>
          )}
          {/* Recording + Re-record grouped so the link sits tight below the card, clear of the reviewBody 28px gap */}
          <div>
          <div className={styles.playbackCard}>
            <span className={styles.playbackLabel}>YOUR RECORDING</span>
            <div className={styles.playerRow}>
              <button
                className={`${styles.playBtn} ${playing ? styles.playBtnPlaying : ''}`}
                onClick={togglePlayback}
                aria-label={playing ? 'Pause playback' : 'Play recording'}
              >
                {playing ? (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <rect x="0" y="0" width="4" height="14" rx="1" />
                    <rect x="8" y="0" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <path d="M0 0l12 7-12 7V0z" />
                  </svg>
                )}
              </button>

              <div className={styles.waveform}>
                {[8,14,10,18,12,20,16,22,14,18,10,16,20,12,8,14,6,10].map((h, i) => (
                  <span
                    key={i}
                    className={`${styles.waveBar} ${i >= 12 ? styles.waveBarLight : ''}`}
                    style={{ height: `${h}px` }}
                  />
                ))}
              </div>

              <span className={styles.playbackDuration}>
                {durationFormatted || (editMemory?.voiceNote?.duration
                  ? `${Math.floor(editMemory.voiceNote.duration / 60)}:${(editMemory.voiceNote.duration % 60).toString().padStart(2, '0')}`
                  : '0:00')}
              </span>
            </div>

            {previewUrl && (
              <audio ref={previewAudioRef} src={previewUrl} className={styles.hiddenAudio} />
            )}
          </div>
          {isEditMode && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => { setWantsReRecord(true); reRecord(); }}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-sage-dark, #5b7a65)', fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Re-record
              </button>
            </div>
          )}
          </div>

          {/* Settings card (label) — create: below the player. (Edit places it above the player.) */}
          {!isEditMode && (
            <div className={styles.settingsCard}>
              <div className={styles.settingsInputRow}>
                <input
                  type="text"
                  className={styles.labelInput}
                  placeholder="Label (optional) — e.g., Advice, Funny moments..."
                  value={memoryLabel}
                  onChange={(e) => setMemoryLabel(e.target.value)}
                />
              </div>
            </div>
          )}

          {uploadError && (
            <p className={styles.uploadError} role="alert">{uploadError}</p>
          )}

          {isEditMode && space?.groupSpacesEnabled && (
            <ContributorEditSharing
              visibilityPref={share.visibilityPref}
              reviewStatus={share.reviewStatus}
              groupSpacesEnabled={space?.groupSpacesEnabled}
              saving={sharingBusy}
              onShare={handleShareTier}
              onMakePrivate={handleMakePrivate}
              space={space}
              api={access.api}
              keepCopyExcluded={share.keepCopyExcluded}
              onKeepCopyChange={handleKeepCopy}
            />
          )}

          {sharingNotice && (
            <p style={{ fontSize: 13, color: '#6b6459', margin: '4px 2px 8px', textAlign: 'center' }}>
              Your edits to this recording aren&rsquo;t saved yet.
            </p>
          )}
          {/* Action buttons — edit: shared compact footer (Re-record moved up); create: save + re-record */}
          {isEditMode ? (
            <ContributorEditActions
              onCancel={() => navigate(`${navBase}/${spaceId}/memories`, { replace: true })}
              onSave={() => handleSave()}
              saving={uploading}
            />
          ) : (
            <div className={styles.reviewActions}>
              {space?.groupSpacesEnabled ? (
                <ContributorSaveActions
                  saving={uploading}
                  onSavePrivate={() => handleSave('contributor_only')}
                  onShare={(vp, kc) => handleSave(vp, kc)}
                space={space}
                api={access.api}
                />
              ) : (
                <button className={styles.saveBtn} onClick={() => handleSave()}>
                  {`Add to ${spaceName}'s space`}
                </button>
              )}
              <button className={styles.reRecordBtn} onClick={() => { setWantsReRecord(true); reRecord(); }}>
                Re-record
              </button>
            </div>
          )}
        </div>

        <ContributorBottomNav spaceId={spaceId} activeTab="record" mode={mode} videoEnabled={space?.contributorVideoEnabled} />

        {showSavedPill && <SavedPill />}
      </div>
    );
  }

  /* ─── 5. Record screen (idle / recording / paused) ─── */
  return (
    <div className={styles.page}>
      <LovedOneBar
        spaceName={spaceName}
        spacePhotoUrl={spacePhotoUrl}
        subtitle={barSubtitle}
        onBack={() => navigate(`${navBase}/${spaceId}/memories`, { replace: true })}
        backLabel="Back to feed"
      />
      {respondQuote}

      <div className={styles.recordingContent}>
        {/* Hardcoded generic prompt banner (no skip — P2) */}
        <div className={styles.promptWrap}>
          <HardcodedPromptBanner spaceName={spaceName} />
        </div>

        <div className={styles.circleArea}>
          <button
            className={circleClass}
            onClick={handleCircleTap}
            aria-label={
              recordingState === 'idle'      ? 'Start recording' :
              recordingState === 'recording' ? 'Pause recording' :
                                               'Resume recording'
            }
          >
            <div className={ringClass} />
            <div className={styles.dot} />
          </button>

          <div className={styles.statusArea}>
            <div className={styles.timerRow}>
              <span className={timerClass}>{durationFormatted}</span>
              <span className={styles.stateLabel}>{stateLabel}</span>
            </div>
            <p className={styles.hint}>{hintText}</p>
          </div>

          {recorderError && (
            <p className={styles.recorderError} role="alert">{recorderError}</p>
          )}

          {(recordingState === 'recording' || recordingState === 'paused') && (
            <div className={styles.controls}>
              <button
                className={styles.stopBtn}
                onClick={stopRecording}
                aria-label="Stop recording"
              >
                <span className={styles.stopIcon} />
                Stop
              </button>
            </div>
          )}
        </div>
      </div>

      <ContributorBottomNav spaceId={spaceId} activeTab="record" mode={mode} videoEnabled={space?.contributorVideoEnabled} />
    </div>
  );
}

/* ═══════════════════════════════════════
   HARDCODED PROMPT BANNER
   Inline component — Session 3 P2 decision. Contributors don't hit the
   backend prompt endpoints (owner /spaces/:id/prompt is JWT-authed). Generic
   guidance only. No skip link (no prompt to advance).

   Visual: mirrors PromptBanner fullWidth mode for visual consistency.
   Uses styles from RecordPage.module.css:
     - .promptWrap wraps (negative margin on record screen, normal on review)
   No dedicated prompt banner classes here — the wrap alone provides the
   container styling; the inner card uses its own inline styles to avoid
   touching PromptBanner.module.css (owner-owned).
   ═══════════════════════════════════════ */

function HardcodedPromptBanner({ spaceName }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #e8f0e8 0%, #d8e8d8 100%)',
        borderTop: '1px solid rgba(124, 152, 133, 0.3)',
        borderBottom: '1px solid rgba(124, 152, 133, 0.3)',
        padding: '20px 24px',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: '11px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#5b7a65',
          marginBottom: '6px',
          fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        }}
      >
        Share a memory
      </span>
      <p
        style={{
          fontSize: '18px',
          fontStyle: 'italic',
          color: '#2d3436',
          margin: 0,
          lineHeight: 1.4,
          fontFamily: 'var(--font-serif, "Lora", Georgia, serif)',
        }}
      >
        Share a memory of {spaceName}.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════
   SAVED PILL TOAST
   Mirrors ContributorWritePage + ContributorPhotoPage SavedPill.
   ═══════════════════════════════════════ */

function SavedPill() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        // Sits above bottom nav on record/review screens (owner pattern has
        // ~72px nav + margin, add extra for contributor here).
        background: 'var(--color-text-primary, #2d3436)',
        color: '#ffffff',
        padding: '12px 20px',
        borderRadius: '24px',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--color-sage, #7c9885)',
          color: '#ffffff',
          fontSize: '11px',
          lineHeight: 1,
        }}
      >
        ✓
      </span>
      Saved
    </div>
  );
}
